import type { ReferrerClass } from "./types.ts";

/** Matches on the parsed hostname (an exact domain or any subdomain of it),
 * never a substring of the URL — "dropbox.com" must not look like x.com. */
function onDomain(host: string, ...domains: string[]): boolean {
  return domains.some((d) => host === d || host.endsWith(`.${d}`));
}

/** google.com, google.co.uk, google.com.au, ... */
const GOOGLE_HOST = /^(?:[a-z0-9-]+\.)*google\.(?:com?\.)?[a-z]{2,3}$/;

const SEARCH_DOMAINS = ["bing.com", "duckduckgo.com", "search.yahoo.com", "ecosia.org", "search.brave.com", "kagi.com", "baidu.com", "yandex.com", "yandex.ru"];

/**
 * Coarse "where did this visitor come from" bucket, stored as
 * `referrer_class`. `ownOrigin` is the site's own origin: a referrer from it
 * is in-site navigation (e.g. /methodology/ -> /), not an outside source.
 * Pure, so it's testable without a DOM (main.ts passes document.referrer and
 * location.origin).
 */
export function classifyReferrer(referrer: string, ownOrigin: string): ReferrerClass {
  if (referrer === "") return "direct";

  let url: URL;
  try {
    url = new URL(referrer);
  } catch {
    return "other";
  }
  if (url.origin === ownOrigin) return "internal";

  const host = url.hostname.toLowerCase();
  if (onDomain(host, "reddit.com", "redd.it")) return "reddit";
  // t.co is X's outbound link wrapper, so most X traffic arrives from it.
  if (onDomain(host, "x.com", "twitter.com", "t.co")) return "x";
  if (onDomain(host, "facebook.com", "fb.com", "fb.me")) return "facebook";
  if (onDomain(host, "bsky.app", "bsky.social")) return "bluesky";
  if (GOOGLE_HOST.test(host) || onDomain(host, ...SEARCH_DOMAINS)) return "search";
  return "other";
}
